import { ExportButton } from '@/common/buttons/ExportButton';
import { TitleButton } from '@/common/buttons/TitleButton';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useNavState } from '@/common/navState/NavStateContext';
import { Title } from '@/common/title/Title';
import { DateUtils } from '@/common/utils/DateUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useState } from 'react';
import { AggCalcTrxLookupViewerGrid } from './AggCalcTrxLookupViewerGrid';

export const AggCalcTrxLookupViewer = () => {
  const { navState, navigateTo } = useNavState();
  const module = useModuleFromRoute();

  const [exportReq, setExportReq] = useState();
  const [incMode, setIncMode] = useState(true);

  const [rightOnOppositeSide, setRightOnOppositeSide] = useState('');
  const [leftOnOppositeSide, setLeftOnOppositeSide] = useState('');

  // Left file
  const leftViewerReqParam = { ...navState.leftReqParam, incMode: incMode };

  // Right file
  const rightViewerReqParam = { ...navState.rightReqParam, incMode: incMode };

  const viewerPluginCode = 'AGG_CALC_TRX_LOOKUP_VIEWER_PLUGIN';

  const updateExportReq = (partial) => {
    const baseExportReq = {
      timeZoneName: DateUtils.timeZoneName(),
      displayName: `${leftViewerReqParam.calc.name} vs ${rightViewerReqParam.calc.name}`,
      module,
      entity: navState.entity,
      uiType: 'LOOKUP_VIEWER',
      viewerPluginCode,
      leftViewerReqParam,
      rightViewerReqParam,
    };

    setExportReq((prev) => ({
      ...baseExportReq,
      ...prev,
      ...partial,
    }));
  };

  const handleLeftOnOppositeSide = (mode) => {
    setLeftOnOppositeSide((prev) => (prev === mode ? '' : mode));
  };

  const handleRightOnOppositeSide = (mode) => {
    setRightOnOppositeSide((prev) => (prev === mode ? '' : mode));
  };

  const handleBothOnOppositeSide = (mode) => {
    handleRightOnOppositeSide(mode);
    handleLeftOnOppositeSide(mode);
  };

  const clickedStyle = { backgroundColor: '#9ad0b2', color: 'black', border: '1.5px solid black' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 1 }}>
        <Title icon={fas.faBalanceScale} title={'Compare Transactions'} />
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ToggleButtonGroup
            value={incMode ? 'included' : 'all'}
            exclusive
            onChange={(e, value) => {
              setIncMode(value === 'included');
            }}
          >
            <ToggleButton value="included">Included</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
          <TitleButton
            icon={fas.faFilter}
            toolTip="Navigate To Rules"
            onClick={() =>
              navigateTo('gp/inc-rule-tree', {
                incDataSrcId: [leftViewerReqParam.data.incDataSrcId, rightViewerReqParam.data.incDataSrcId],
              })
            }
          />
          <ExportButton exportReq={exportReq} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, height: '100%', overflow: 'hidden', m: 0 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {leftViewerReqParam.calc.name}
          </Typography>
          <AggCalcTrxLookupViewerGrid
            viewerReqParam={leftViewerReqParam}
            entity={navState.entity}
            onOppositeSide={leftOnOppositeSide}
            incMode={incMode}
            side="left"
            onExportReqChange={updateExportReq}
          />
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {rightViewerReqParam.calc.name}
          </Typography>
          <AggCalcTrxLookupViewerGrid
            viewerReqParam={rightViewerReqParam}
            entity={navState.entity}
            onOppositeSide={rightOnOppositeSide}
            incMode={incMode}
            side="right"
            onExportReqChange={updateExportReq}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, py: 2, borderTop: '1px solid #eee' }}>
        <Button
          onClick={() => handleLeftOnOppositeSide('false')}
          style={leftOnOppositeSide === 'false' ? clickedStyle : {}}
        >
          Left only
        </Button>
        <Button
          variant="outlined"
          onClick={() => handleBothOnOppositeSide('true')}
          style={leftOnOppositeSide === 'true' && rightOnOppositeSide === 'true' ? clickedStyle : {}}
        >
          Matches
        </Button>
        <Button
          onClick={() => handleRightOnOppositeSide('false')}
          style={rightOnOppositeSide === 'false' ? clickedStyle : {}}
        >
          Right only
        </Button>
      </Box>
    </Box>
  );
};
